import { useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

function normalizeProviderId(providerId) {
  if (!providerId || providerId === 'null' || providerId === 'undefined') return null;
  return providerId;
}

async function getExistingSubscription(registration) {
  try {
    return await registration.pushManager.getSubscription();
  } catch (_) {
    return null;
  }
}

function arrayBufferEquals(left, right) {
  if (!left || !right || left.byteLength !== right.byteLength) return false;

  const leftView = new Uint8Array(left);
  const rightView = new Uint8Array(right);
  for (let index = 0; index < leftView.length; index += 1) {
    if (leftView[index] !== rightView[index]) return false;
  }
  return true;
}

function subscriptionMatchesPublicKey(subscription, applicationServerKey) {
  const existingKey = subscription?.options?.applicationServerKey;
  if (!existingKey) return true;
  return arrayBufferEquals(existingKey, applicationServerKey);
}

function isIOSDevice() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandaloneApp() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export default function usePushNotifications({ enabled, role, userId, providerId }) {
  useEffect(() => {
    if (!enabled || !userId) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      return;
    }

    let cancelled = false;

    const subscribe = async ({ fromUserGesture = false } = {}) => {
      try {
        const needsIOSGesture = isIOSDevice();
        if (needsIOSGesture && !isStandaloneApp()) return;

        const { data } = await axios.get(`${API}/api/push/vapid-public-key`);
        const publicKey = data?.publicKey;
        if (!publicKey || cancelled) return;

        const registration = await navigator.serviceWorker.register('/sw.js');
        if (cancelled) return;

        let permission = Notification.permission;
        if (permission === 'default') {
          if (needsIOSGesture && !fromUserGesture) return;
          permission = await Notification.requestPermission();
        }
        if (permission !== 'granted' || cancelled) return;

        const applicationServerKey = urlBase64ToUint8Array(publicKey);
        let subscription = await getExistingSubscription(registration);
        if (subscription && !subscriptionMatchesPublicKey(subscription, applicationServerKey)) {
          await subscription.unsubscribe();
          subscription = null;
        }

        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          });
        }

        await axios.post(`${API}/api/push/subscribe`, {
          user_id: userId,
          role,
          provider_id: normalizeProviderId(providerId),
          subscription: subscription.toJSON(),
        });
      } catch (error) {
        console.warn('Push notification setup failed:', error?.message || error);
      }
    };

    const shouldWaitForIOSGesture = isIOSDevice() && isStandaloneApp() && Notification.permission === 'default';
    if (shouldWaitForIOSGesture) {
      let gestureHandled = false;
      const subscribeFromGesture = () => {
        if (gestureHandled) return;
        gestureHandled = true;
        subscribe({ fromUserGesture: true });
      };
      window.addEventListener('click', subscribeFromGesture, { once: true });
      window.addEventListener('touchend', subscribeFromGesture, { once: true });

      return () => {
        cancelled = true;
        window.removeEventListener('click', subscribeFromGesture);
        window.removeEventListener('touchend', subscribeFromGesture);
      };
    }

    subscribe();

    return () => {
      cancelled = true;
    };
  }, [enabled, providerId, role, userId]);
}
